import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePipelineGates } from "@/hooks/usePipelineGates";
import type { PipelineRun } from "@/hooks/useProjectPipelines";
import type { GateInfo } from "@/api/pipelineGates";
import { formatDateTime } from "@/lib/date";

interface RunDetailSheetProps {
  run: PipelineRun | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function GateStatusBadge({ status }: { status: GateInfo["status"] }) {
  const { t } = useTranslation();

  if (status === "approved") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300">
        {t("projectPortal.pipelines.gates.approved")}
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge variant="destructive">
        {t("projectPortal.pipelines.gates.rejected")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-yellow-500 text-yellow-700">
      {t("projectPortal.pipelines.gates.pending")}
    </Badge>
  );
}

interface GateRowProps {
  gate: GateInfo;
  onApprove: (phaseId: string) => void;
  onReject: (phaseId: string, reason: string) => void;
  deciding: boolean;
}

function GateRow({ gate, onApprove, onReject, deciding }: GateRowProps) {
  const { t } = useTranslation();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const isPending = gate.status === "pending";

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`gate-row-${gate.phase_id}`}>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="font-medium text-sm">{gate.phase_name}</div>
          {gate.gate_note && (
            <p className="text-xs text-muted-foreground">{gate.gate_note}</p>
          )}
          {gate.created_at && (
            <p className="text-xs text-muted-foreground">{formatDateTime(gate.created_at)}</p>
          )}
        </div>
        <GateStatusBadge status={gate.status} />
      </div>

      {isPending && !showRejectInput && (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={deciding}
            onClick={() => { onApprove(gate.phase_id); }}
          >
            {deciding && <Loader2 className="h-3 w-3 animate-spin" />}
            {t("projectPortal.pipelines.gates.approve")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={deciding}
            onClick={() => { setShowRejectInput(true); }}
          >
            {t("projectPortal.pipelines.gates.reject")}
          </Button>
        </div>
      )}

      {isPending && showRejectInput && (
        <div className="space-y-2">
          <textarea
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder={t("projectPortal.pipelines.gates.rejectReason")}
            value={rejectReason}
            onChange={(e) => { setRejectReason(e.target.value); }}
            rows={2}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={deciding || rejectReason.trim().length === 0}
              onClick={() => { onReject(gate.phase_id, rejectReason.trim()); }}
            >
              {deciding && <Loader2 className="h-3 w-3 animate-spin" />}
              {t("projectPortal.pipelines.gates.confirmReject")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowRejectInput(false);
                setRejectReason("");
              }}
            >
              {t("projectPortal.pipelines.gates.cancelReject")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RunDetailSheet({ run, open, onOpenChange }: RunDetailSheetProps) {
  const { t } = useTranslation();
  const runId = run?.run_id ?? null;
  const { gates, loading, error, approve, reject, deciding, decisionError } =
    usePipelineGates(open ? runId : null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("projectPortal.pipelines.gates.drawerTitle")}</SheetTitle>
          <SheetDescription>
            {run ? run.run_id.slice(0, 8) : ""}
          </SheetDescription>
        </SheetHeader>

        {run && (
          <div className="mt-4 space-y-4">
            {/* Run summary */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("projectPortal.pipelines.gates.drawerPipeline")}
                </span>
                <span>{run.pipeline_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("projectPortal.pipelines.gates.drawerStatus")}
                </span>
                <span>{run.status}</span>
              </div>
              {run.started_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("projectPortal.pipelines.gates.drawerStarted")}
                  </span>
                  <span>{formatDateTime(run.started_at)}</span>
                </div>
              )}
            </div>

            {/* Gates section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">
                {t("projectPortal.pipelines.gates.title")}
              </h3>

              {loading && (
                <div className="flex items-center gap-2 py-4 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive">
                  {t("projectPortal.pipelines.gates.errorLoad")}
                </p>
              )}

              {decisionError && (
                <p className="text-sm text-destructive">
                  {t(
                    decisionError === "conflict"
                      ? "projectPortal.pipelines.gates.errorConflict"
                      : "projectPortal.pipelines.gates.errorGeneric",
                  )}
                </p>
              )}

              {!loading && !error && gates.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t("projectPortal.pipelines.gates.empty")}
                </p>
              )}

              {gates.map((gate) => (
                <GateRow
                  key={gate.phase_id}
                  gate={gate}
                  onApprove={approve}
                  onReject={reject}
                  deciding={deciding}
                />
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
